import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import * as LucideIcons from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { showToast } from '../../../common/ToastHandler';

interface ActionBoostBlockProps {
  column: any;
  rowData: any;
  onDataReload?: () => void;
}

const ActionBoostBlock: React.FC<ActionBoostBlockProps> = ({
  column,
  rowData,
  onDataReload,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const [isLoading, setIsLoading] = useState(false);

  // Get available actions from row data to determine current state
  const availableActions = rowData.actions || [];
  const canBoost = availableActions.includes('boost');
  const canUnboost = availableActions.includes('unboost');
  const canBlock = availableActions.includes('block');
  const canUnblock = availableActions.includes('unblock');

  // Determine current state based on available actions
  const isCurrentlyBoosted = canUnboost; // If can unboost, then currently boosted
  const isCurrentlyBlocked = canUnblock; // If can unblock, then currently blocked

  const executeAction = async (action: any, actionType: string) => {
    setIsLoading(true);
    showToast('info', `Processing ${actionType}...`, 2000);

    // Replace template variables in URL and body
    const url = action.url.replace(/\{([^}]+)\}/g, (_, key) => {
      return rowData[key] || '';
    });

    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken.__raw;

      let body = action.body;
      if (body) {
        try {
          body = JSON.parse(body);

          Object.keys(body).forEach(key => {
            const value = body[key];
            if (typeof value === 'string' && value.includes('{')) {
              const match = value.match(/\{([^}]+)\}/);
              if (match && match[1]) {
                const row_data_key = match[1];
                body[key] = rowData[row_data_key] || '';
              }
            }
          });

          body = JSON.stringify(body);
        } catch (error) {
          console.error('Failed to parse action body:', error);
          toast.dismiss();
          showToast('error', 'Failed to process request data');
          setIsLoading(false);
          return;
        }
      }

      const response = await axios({
        method: action.method,
        url: url,
        data: body,
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
          ...(action.headers || {}),
        },
      });

      // Dismiss progress toast
      toast.dismiss();

      const status_handler =
        action.response?.status_handlers?.find(
          handler => handler.status_code === response.status
        ) || action.response?.default_handler;

      if (status_handler) {
        if (status_handler.show_toast) {
          showToast(
            status_handler.toast_type || 'success',
            status_handler.message
          );
        }
      } else {
        showToast('success', `${actionType} completed successfully`);
      }

      // Check for refresh/reload at the response level
      if (action.response?.refresh_page || action.response?.reload_data) {
        if (onDataReload) {
          onDataReload();
        } else {
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Action failed:', error);
      toast.dismiss();
      showToast(
        'error',
        `Failed to ${actionType.toLowerCase()}. Please try again.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleBoostClick = async () => {
    if (isLoading) return;

    // Determine which action to perform based on current state
    const actionId = isCurrentlyBoosted ? 'unboost' : 'boost';
    const actionType = isCurrentlyBoosted ? 'Unboost' : 'Boost';

    // Find the appropriate action configuration from column
    let actionConfig = column.sub_keys.find(subKey => subKey.id === actionId);

    // If specific action not found, try to use boost config and modify it
    if (!actionConfig && actionId === 'unboost') {
      const boostConfig = column.sub_keys.find(subKey => subKey.id === 'boost');
      if (boostConfig) {
        actionConfig = { ...boostConfig };
        // Modify the action to send unboost tag instead of boost
        try {
          const bodyObj = JSON.parse(actionConfig.icon.action.body);
          bodyObj.tags = ['unboost']; // Send unboost tag
          actionConfig.icon.action.body = JSON.stringify(bodyObj);
        } catch (error) {
          console.error('Failed to modify action body for unboost:', error);
        }
      }
    }

    if (!actionConfig) {
      showToast('error', `${actionType} configuration not found`);
      return;
    }

    await executeAction(actionConfig.icon.action, actionType);
  };

  const handleBlockClick = async () => {
    if (isLoading) return;

    // Determine which action to perform based on current state
    const actionId = isCurrentlyBlocked ? 'unblock' : 'block';
    const actionType = isCurrentlyBlocked ? 'Unblock' : 'Block';

    // Find the appropriate action configuration from column
    let actionConfig = column.sub_keys.find(subKey => subKey.id === actionId);

    // If specific action not found, try to use block config and modify it
    if (!actionConfig && actionId === 'unblock') {
      const blockConfig = column.sub_keys.find(subKey => subKey.id === 'block');
      if (blockConfig) {
        actionConfig = { ...blockConfig };
        // Modify the action to send unblock tag instead of block
        try {
          const bodyObj = JSON.parse(actionConfig.icon.action.body);
          bodyObj.tags = ['unblock']; // Send unblock tag
          actionConfig.icon.action.body = JSON.stringify(bodyObj);
        } catch (error) {
          console.error('Failed to modify action body for unblock:', error);
        }
      }
    }

    if (!actionConfig) {
      showToast('error', `${actionType} configuration not found`);
      return;
    }

    await executeAction(actionConfig.icon.action, actionType);
  };

  const renderActionButton = (
    actionId: string,
    buttonText: string,
    buttonClass: string,
    onClick: () => void,
    tooltip: string
  ) => {
    return (
      <button
        onClick={onClick}
        className={`${buttonClass} ${isLoading ? 'disabled' : ''}`}
        title={tooltip}
        disabled={isLoading}
        style={{
          opacity: isLoading ? 0.6 : 1,
        }}
      >
        {isLoading ? (
          <>
            <LucideIcons.Loader2
              size={14}
              className="animate-spin"
              style={{ marginRight: '5px' }}
            />
            Processing...
          </>
        ) : (
          buttonText
        )}
      </button>
    );
  };

  return (
    <div className="action-btns">
      {/* Boost/Unboost Button - only show if user can boost or unboost */}
      {(canBoost || canUnboost) &&
        renderActionButton(
          'boost',
          isCurrentlyBoosted ? 'Un-boost' : 'Boost',
          isCurrentlyBoosted ? 'action_btn_unboost' : 'action_btn_boost',
          handleBoostClick,
          isCurrentlyBoosted
            ? 'Click to unboost this source'
            : 'Click to boost this source'
        )}

      {/* Block/Unblock Button - only show if user can block or unblock */}
      {(canBlock || canUnblock) &&
        renderActionButton(
          'block',
          isCurrentlyBlocked ? 'Un-block' : 'Block',
          isCurrentlyBlocked ? 'action_btn_unblock' : 'action_btn_block',
          handleBlockClick,
          isCurrentlyBlocked
            ? 'Click to unblock this source'
            : 'Click to block this source'
        )}
    </div>
  );
};

export default ActionBoostBlock;
